import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Download, FileText, BookOpen } from 'lucide-react';
import axios from 'axios';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BibliothequeDocuments = () => {
  const { t } = useTranslation();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState('gros_oeuvre');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bibliotheque`);
      setDocuments(response.data);
    } catch {
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      gros_oeuvre: t('bibliotheque.gros_oeuvre', 'Gros Œuvre'),
      second_oeuvre: t('bibliotheque.second_oeuvre', 'Second Œuvre'),
      travaux_publics: t('bibliotheque.travaux_publics', 'Travaux Publics'),
      intellectuels: t('bibliotheque.intellectuels', 'Professions Intellectuelles')
    };
    return labels[category] || category;
  };

  const handleViewDocument = async (doc) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bibliotheque/${doc.id}`);
      setSelectedDocument(response.data);
      setDialogOpen(true);
    } catch {
      alert('Erreur lors du chargement du document. Veuillez réessayer.');
    }
  };

  const handleDownloadDocument = async (doc) => {
    try {
      // Si le document n'a pas de contenu, le charger d'abord
      let documentWithContent = doc;
      if (!doc.contenu) {
        const response = await axios.get(`${BACKEND_URL}/api/bibliotheque/${doc.id}`);
        documentWithContent = response.data;
      }

      // Vérifier que le contenu existe
      if (!documentWithContent.contenu) {
        alert('Ce document ne contient pas de contenu téléchargeable.');
        return;
      }

      // Créer un blob avec le contenu et télécharger
      const blob = new Blob([documentWithContent.contenu], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${documentWithContent.titre}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Erreur lors du téléchargement du document. Veuillez réessayer.');
    }
  };

  const filteredDocuments = documents.filter(doc => doc.categorie === activeCategory);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Bouton Retour */}
      <div className="mb-4">
        <BackButton label="Retour" />
      </div>
      <div className="flex items-center gap-3">
        <BookOpen className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold">Bibliothèque de Documents</h1>
          <p className="text-gray-600">Modèles de procès-verbaux et documents types pour vos chantiers</p>
        </div>
      </div>

      <Tabs value={activeCategory} onValueChange={setActiveCategory} className="w-full">
        <TabsList className="grid w-full grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          <TabsTrigger value="gros_oeuvre">Gros Œuvre</TabsTrigger>
          <TabsTrigger value="second_oeuvre">Second Œuvre</TabsTrigger>
          <TabsTrigger value="travaux_publics">Travaux Publics</TabsTrigger>
          <TabsTrigger value="intellectuels">Professionnels Intellectuels</TabsTrigger>
        </TabsList>

        <TabsContent value={activeCategory} className="mt-6">
          {filteredDocuments.length === 0 ? (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-gray-500">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Aucun document disponible dans cette catégorie</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredDocuments.map((doc) => (
                <Card key={doc.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="h-5 w-5 text-blue-600" />
                          {doc.titre}
                        </CardTitle>
                        <CardDescription className="mt-2">{doc.description}</CardDescription>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Badge className="bg-purple-700 text-white font-semibold">{getCategoryLabel(doc.categorie)}</Badge>
                      {doc.prix === 0 ? (
                        <Badge variant="success" className="bg-green-700 text-white font-semibold">Gratuit</Badge>
                      ) : (
                        <Badge className="bg-green-700 text-white font-semibold">{doc.prix}€</Badge>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleViewDocument(doc)}
                        variant="default"
                        className="flex-1"
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Voir le modèle
                      </Button>
                      <Button
                        onClick={() => handleDownloadDocument(doc)}
                        variant="outline"
                        className="flex-1"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Télécharger
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog pour afficher le contenu du document */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedDocument?.titre}</DialogTitle>
            <DialogDescription>{selectedDocument?.description}</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <div className="bg-gray-50 p-6 rounded-lg border">
              <pre className="whitespace-pre-wrap text-sm font-mono">
                {selectedDocument?.contenu}
              </pre>
            </div>
            <div className="mt-4 flex gap-2">
              <Button
                onClick={() => handleDownloadDocument(selectedDocument)}
                className="flex-1"
              >
                <Download className="h-4 w-4 mr-2" />
                Télécharger ce modèle
              </Button>
              <Button
                onClick={() => setDialogOpen(false)}
                variant="outline"
                className="flex-1"
              >
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BibliothequeDocuments;
